import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCheck, CircleCheck, Clock, Loader2, Zap } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import { EventStatusBadge, SeverityBadge } from "@/components/common/SeverityBadge";
import { api, errorMessage } from "@/lib/api";
import { durationLabel, fmtDateTime, fmtRelative } from "@/lib/format";

const Row = ({ label, children }) => (
  <div className="flex items-start justify-between gap-4 py-2.5 text-[13px]">
    <span className="shrink-0 text-muted-foreground">{label}</span>
    <span className="text-right font-medium">{children}</span>
  </div>
);

export const EventDetailSheet = ({ event, open, onOpenChange, onUpdated, showPlantLink = true }) => {
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(null);

  const act = async (action) => {
    if (!event) return;
    setBusy(action);
    try {
      const { data } = await api.post(`/events/${event.id}/${action}`, { comment: comment || null });
      toast.success(action === "ack" ? "Hendelsen er kvittert" : "Hendelsen er lukket");
      onUpdated?.({ ...event, ...data });
      setComment("");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col gap-0 overflow-y-auto p-0 sm:max-w-md" data-testid="event-detail-sheet">
        {event ? (
          <>
            <SheetHeader className="border-b border-border px-6 py-5 pr-12">
              <div className="flex flex-wrap items-center gap-2.5">
                <SeverityBadge severity={event.severity} />
                <EventStatusBadge status={event.status} />
                {event.production_stop ? (
                  <span className="mono inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.06em] text-muted-foreground">
                    <Zap className="h-3 w-3" aria-hidden="true" /> Produksjonsstans
                  </span>
                ) : null}
              </div>
              <SheetTitle className="pt-1.5 text-[18px] leading-snug tracking-tight" data-testid="event-detail-title">
                {event.title}
              </SheetTitle>
              <SheetDescription>
                {event.plant_name ? (
                  showPlantLink ? (
                    <Link to={`/kraftverk/${event.plant_id}`} className="font-medium text-primary hover:underline" data-testid="event-detail-plant-link">
                      {event.plant_name}
                    </Link>
                  ) : (
                    event.plant_name
                  )
                ) : null}
                {" · "}
                {event.kind_label}
              </SheetDescription>
            </SheetHeader>
            <div className="px-6 py-5">
              <p className="text-[13px] leading-relaxed text-muted-foreground">{event.description}</p>
              <div className="mt-5 hairline-y border-t border-border">
                <Row label="Startet">
                  <span className="mono text-[12.5px]">{fmtDateTime(event.started_at)}</span> <span className="text-xs font-normal text-muted-foreground">({fmtRelative(event.started_at)})</span>
                </Row>
                <Row label="Avsluttet">
                  {event.ended_at ? (
                    <span className="mono text-[12.5px]">{fmtDateTime(event.ended_at)}</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-300">
                      <Clock className="h-3.5 w-3.5" aria-hidden="true" /> Pågår
                    </span>
                  )}
                </Row>
                <Row label="Varighet">{durationLabel(event.duration_h)}</Row>
                <Row label="Kilde">{event.source}</Row>
                {event.acknowledged_by ? (
                  <Row label="Kvittert av">
                    {event.acknowledged_by}
                    <div className="mono text-[11px] font-normal text-muted-foreground">{fmtDateTime(event.acknowledged_at)}</div>
                    {event.ack_comment ? <div className="mt-0.5 text-xs font-normal italic text-muted-foreground">«{event.ack_comment}»</div> : null}
                  </Row>
                ) : null}
                {event.resolved_by ? (
                  <Row label="Lukket av">
                    {event.resolved_by}
                    <div className="mono text-[11px] font-normal text-muted-foreground">{fmtDateTime(event.resolved_at)}</div>
                  </Row>
                ) : null}
              </div>
            </div>
            {event.status !== "resolved" ? (
              <div className="mt-auto border-t border-border bg-muted/40 px-6 py-5">
                <div className="label mb-2">Oppfølging</div>
                <Textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2} placeholder="Kommentar (valgfritt)" className="bg-card" data-testid="event-ack-comment" />
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {event.status === "open" ? (
                    <Button size="sm" onClick={() => act("ack")} disabled={!!busy} data-testid="event-ack-button">
                      {busy === "ack" ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <CheckCheck className="h-4 w-4" aria-hidden="true" />}
                      Kvitter
                    </Button>
                  ) : null}
                  {event.ended_at ? (
                    <Button size="sm" variant="outline" onClick={() => act("resolve")} disabled={!!busy} data-testid="event-resolve-button">
                      {busy === "resolve" ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <CircleCheck className="h-4 w-4" aria-hidden="true" />}
                      Lukk hendelse
                    </Button>
                  ) : (
                    <span className="text-xs text-muted-foreground">Kan lukkes når hendelsen er avsluttet i driftssystemet.</span>
                  )}
                </div>
              </div>
            ) : null}
          </>
        ) : null}
      </SheetContent>
    </Sheet>
  );
};
